import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Popconfirm, Space, Input, Select, Typography, message, Modal, Form, Radio } from 'antd';
import { DeleteOutlined, SearchOutlined, StopOutlined, CheckOutlined, SoundOutlined, MutedOutlined } from '@ant-design/icons';
import api from '../api';

const { Title } = Typography;

function UserManagePage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [muteModal, setMuteModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [muteHours, setMuteHours] = useState(24);

  const loadUsers = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20, showAll: showAll ? 1 : 0 };
      if (keyword) params.keyword = keyword;
      const { list, total: t } = await api.get('/admin/users', { params });
      setUsers(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [showAll]);

  const handleSearch = () => loadUsers(1);

  const handleDisable = async (id) => {
    await api.put(`/admin/users/${id}/disable`);
    message.success('用户已禁用');
    loadUsers(page);
  };

  const handleEnable = async (id) => {
    await api.put(`/admin/users/${id}/enable`);
    message.success('用户已启用');
    loadUsers(page);
  };

  const handleMute = (record) => {
    setSelected(record);
    setMuteModal(true);
  };

  const handleMuteConfirm = async () => {
    await api.put(`/admin/users/${selected.id}/mute`, { hours: muteHours });
    message.success(`已禁言 ${muteHours} 小时`);
    setMuteModal(false);
    loadUsers(page);
  };

  const handleUnmute = async (id) => {
    await api.put(`/admin/users/${id}/unmute`);
    message.success('已取消禁言');
    loadUsers(page);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      message.success('用户已删除');
      loadUsers(page);
    } catch (e) {
      const msg = (e && e.error) ? e.error : '删除失败，请重试';
      message.error('删除失败：' + msg);
    }
  };

  const handleVerifyEmail = async (id) => {
    await api.put(`/admin/users/${id}/verify-email`);
    message.success('已标记为邮箱已验证');
    loadUsers(page);
  };

  const handleResend = async (id) => {
    await api.post(`/admin/users/${id}/resend-verify-email`);
    message.success('验证邮件已重新发送');
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '昵称', dataIndex: 'nickname', width: 150 },
    { title: '手机号', dataIndex: 'phone', width: 150 },
    { title: '邮箱', dataIndex: 'email', width: 180, ellipsis: true, render: (v) => v || '-' },
    {
      title: '邮箱验证', width: 110,
      render: (_, r) =>
        r.email_verified === 1 ? <Tag color="green">已验证</Tag>
          : (r.email ? <Tag color="orange">未验证</Tag> : <Tag>无邮箱</Tag>),
    },
    { title: '签名', dataIndex: 'signature', ellipsis: true },
    { title: '性别', dataIndex: 'gender', width: 80, render: (v) => v === 1 ? '男' : v === 2 ? '女' : '-' },
    { title: '登录次数', dataIndex: 'login_count', width: 100 },
    { title: '注册时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '状态', width: 120,
      render: (_, r) => {
        let statusTag = r.status === 1 ? <Tag color="green">正常</Tag> : <Tag color="red">已禁用</Tag>;
        if (r.muted === 1 && r.mute_until && new Date(r.mute_until) > new Date()) {
          statusTag = <Tag color="orange">禁言中</Tag>;
        }
        return statusTag;
      },
    },
    {
      title: '操作', width: 280,
      render: (_, record) => (
        <Space size="small">
          {record.status === 1 ? (
            <Popconfirm title="确定禁用该用户?" onConfirm={() => handleDisable(record.id)}>
              <Button size="small" danger icon={<StopOutlined />}>禁用</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title="确定启用该用户?" onConfirm={() => handleEnable(record.id)}>
              <Button size="small" type="primary" icon={<CheckOutlined />}>启用</Button>
            </Popconfirm>
          )}
          {record.muted === 1 && record.mute_until && new Date(record.mute_until) > new Date() ? (
            <Button size="small" icon={<MutedOutlined />} onClick={() => handleUnmute(record.id)}>解除禁言</Button>
          ) : (
            <Button size="small" icon={<SoundOutlined />} onClick={() => handleMute(record)}>禁言</Button>
          )}
          {record.email && record.email_verified !== 1 && (
            <>
              <Popconfirm title="确定标记为已验证?" onConfirm={() => handleVerifyEmail(record.id)}>
                <Button size="small" type="link">验邮箱</Button>
              </Popconfirm>
              <Button size="small" type="link" onClick={() => handleResend(record.id)}>重发</Button>
            </>
          )}
          <Popconfirm title="确定删除该用户?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>用户管理</Title>
        <Space>
          <Select
            value={showAll ? 'all' : 'active'}
            onChange={(v) => {
              setShowAll(v === 'all');
              setPage(1);
            }}
            style={{ width: 120 }}
            options={[
              { value: 'active', label: '正常用户' },
              { value: 'all', label: '全部用户' },
            ]}
          />
          <Input
            placeholder="搜索昵称或手机号"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: loadUsers,
          showSizeChanger: false,
        }}
        size="small"
      />
      <Modal
        title={`禁言用户 - ${selected?.nickname || ''}`}
        open={muteModal}
        onOk={handleMuteConfirm}
        onCancel={() => setMuteModal(false)}
      >
        <Radio.Group value={muteHours} onChange={(e) => setMuteHours(e.target.value)}>
            <Radio value={1}>禁言 1 小时</Radio>
            <Radio value={6}>禁言 6 小时</Radio>
            <Radio value={12}>禁言 12 小时</Radio>
            <Radio value={24}>禁言 24 小时</Radio>
            <Radio value={72}>禁言 3 天</Radio>
            <Radio value={168}>禁言 7 天</Radio>
            <Radio value={720}>禁言 30 天</Radio>
        </Radio.Group>
      </Modal>
    </>
  );
}

export default UserManagePage;
